import React, { useMemo, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { useT, useLanguage, useLocalized } from "../../lib/LanguageContext";
import { pluralKey } from "../../lib/pluralKey";
import LoadFailure from "../../components/ui/LoadFailure";
import GlobalDataTable from "../GlobalDataTable";
import { NoAccessPanel, isForbidden } from "../NoAccess";
import { usePagedList } from "../usePagedList";
import { SECTIONS, sectionByKey } from "./sectionColumns";

/**
 * Tenant 360 -> the section tiles and the drill-down under them
 * (docs/auth-and-admin/super-admin-s7-tenant360-contract.md section 1.4).
 *
 * Props: `tenantId` (string), `sections` (the `/360` block: `{ <key>: { total: int, readable: bool } }`
 * for the fifteen registry keys). A tile shows its total (zero included); a section the
 * operator's role cannot read is locked and, when opened, answers with the shared no-access
 * panel without sending a request. An opened section is read server-paged from
 * `GET /super-admin/tenants/{tenantId}/section/{key}` and shown as the same table Global Data uses.
 */

const PAGE_SIZE = 25;

// The two money sections say whose money they hold; the figures themselves carry their currency.
const MONEY_SECTION_NOTES = {
  transactions: "t360.note.transactions",
  client_transactions: "t360.note.clientTransactions",
};

/** Asc -> desc -> asc on the same column; a new column starts ascending. */
const nextSort = (current, key) => (current === key ? `-${key}` : key);

/**
 * One section's rows. Mounted with `key={tenantId:section}`, so a section or practice change is
 * a remount: no row, total, page or sort of the previous one can appear under the new heading,
 * and a read still in flight for it is dropped by the hook's cleanup.
 */
function SectionTable({ tenantId, section }) {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const loc = useLocalized();
  const def = sectionByKey(section);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(def.defaultSort);
  const { data, loading, loadFailed, loadError, retry } = usePagedList({
    path: `/super-admin/tenants/${encodeURIComponent(tenantId)}/section/${section}`,
    filters: { sort },
    page,
    setPage,
    pageSize: PAGE_SIZE,
  }); // data: { section, total, items, limit, skip }
  const ctx = useMemo(() => ({ t, lang, loc }), [t, lang, loc]);
  const changeSort = (key) => { setSort((cur) => nextSort(cur, key)); setPage(1); };

  return (
    <>
      {data && !loadFailed && (
        <span className="block text-slate-400 text-sm mb-3" data-testid="t360-section-total">
          {t(pluralKey(lang, data.total, "gdv.total"), { total: data.total })}
        </span>
      )}
      {MONEY_SECTION_NOTES[section] && (
        <p className="text-xs text-amber-600 mb-3" data-testid={`t360-note-${section}`}>{t(MONEY_SECTION_NOTES[section])}</p>
      )}
      {loadFailed && !loading && isForbidden(loadError) && <NoAccessPanel testId="t360s-forbidden" />}
      <LoadFailure
        message={loadFailed && !loading && !isForbidden(loadError) ? tc("errors.loadFailed") : ""}
        onRetry={retry}
        retryLabel={t("sal.retry")}
        testId="t360s-load-error"
        retryTestId="t360s-load-error-retry"
      />
      {loading && (loadFailed || !data) && (
        <span data-testid="t360s-loading"><Loader2 className="w-5 h-5 animate-spin text-amber-600" /></span>
      )}
      {data && !loadFailed && (
        <div className={`rounded-xl border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
          <GlobalDataTable
            columns={def.columns}
            items={data.items}
            total={data.total}
            page={page}
            pageSize={PAGE_SIZE}
            onPage={setPage}
            sort={sort}
            onSort={changeSort}
            ctx={ctx}
            testIdPrefix="t360s"
            emptyText={t("gdv.empty")}
          />
        </div>
      )}
    </>
  );
}

export default function SectionsPanel({ tenantId, sections }) {
  const t = useT("superadmin");
  const [open, setOpen] = useState(null);
  const opened = open ? sectionByKey(open) : null;
  const openedReadable = opened ? sections?.[opened.key]?.readable === true : false;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const info = sections?.[s.key];
          const selected = open === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setOpen(s.key)}
              data-testid={`section-${s.key}`}
              className={`text-left p-4 rounded-xl border transition ${selected ? "bg-amber-100 border-amber-200" : "bg-white border-slate-200 hover:bg-slate-50"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-4 h-4 ${selected ? "text-amber-600" : "text-slate-500"}`} />
                <span className="flex items-center gap-1.5">
                  {info && info.readable === false && (
                    <span data-testid={`section-lock-${s.key}`} title={t("t360s.locked")}>
                      <Lock className="w-3.5 h-3.5 text-slate-400" aria-label={t("t360s.locked")} />
                    </span>
                  )}
                  <span className="text-2xl font-sol font-semibold" data-testid={`section-count-${s.key}`}>{info ? info.total : "—"}</span>
                </span>
              </div>
              <div className="text-xs text-slate-500">{t(s.labelKey)}</div>
            </button>
          );
        })}
      </div>

      {opened && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-6" data-testid="t360-section">
          <h3 className="font-sol font-semibold text-xl mb-1" data-testid="t360-section-heading">{t(opened.labelKey)}</h3>
          {openedReadable
            ? <SectionTable key={`${tenantId}:${opened.key}`} tenantId={tenantId} section={opened.key} />
            : <NoAccessPanel testId="t360s-forbidden" className="mt-3" />}
        </div>
      )}
    </>
  );
}
